import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useStripe } from '@stripe/react-stripe-js';
import { useSearch, useNavigate } from '@tanstack/react-location';
import { isFunction } from 'lodash-es';
import { PaymentOriginEnum } from '@frontend/api-invoices';
import { convertToCents } from '@frontend/currency';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { PaymentConfigName } from '@frontend/payments-stripe-wrapper';
import { genUUIDV4 } from '@frontend/string';
import { theme } from '@frontend/theme';
import { useAlert, useModalControl } from '@frontend/design-system';
import StripeIcon from '../assets/powered-by-stripe.svg?react';
import {
  AttachmentAuth,
  LocationInformation,
  LogoWrapper,
  OpenAttachmentButton,
  PaymentConfirmationModal,
  PortalPageWrapper,
  RequestedAmount,
  StripePrivacyPolicyFooter,
  SuccessCard,
} from '../atoms';
import { useStripePortalPayment } from '../hooks';
import { useSubmitPaymentStripe, StripeElementsPortalWrapper } from '../stripe-components';
import {
  AffirmRedirectParams,
  DisabledHandler,
  FailureHandler,
  LocationPortalData,
  SuccessHandler,
  SuccessPayment,
} from '../types';
import { StripeStepperBody } from './stripe-stepper-body';
import { StripeStepperProvider, useStripeStepper } from './stripe-stepper-context';

const styles = {
  stepperContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
  stripeIcon: css`
    height: 20px;
    margin-left: ${theme.spacing(3)};
    vertical-align: middle;
  `,
};

interface PaymentPortalStepperProps {
  amount?: number;
  attachment?: string | null;
  captchaToken?: string | null;
  locationData?: LocationPortalData;
  onBehalfOf: string;
  onDisabled?: DisabledHandler;
  onFailure?: FailureHandler;
  onSuccess?: SuccessHandler;
  origin: PaymentOriginEnum;
  paymentConfigName?: PaymentConfigName;
  person?: {
    name?: string;
    id?: string;
  };
  refetch?: () => void;
  requestId?: string;
  resetForm?: () => void;
  settings?: {
    allowSaveACHOnFile?: boolean;
    allowSaveCardOnFile?: boolean;
    amountEditable?: boolean;
    oneTimeRequest?: boolean;
    showExpressElement?: boolean;
    showLocationInfo?: boolean;
    showAttachment?: boolean;
    skipAttachmentAuth?: boolean;
  };
  setupIntentId?: string;
}

const StripePaymentPortalStepperContainer = ({
  amount,
  attachment,
  captchaToken: captchaTokenFromProps = null,
  locationData,
  onDisabled,
  onFailure,
  onSuccess,
  origin,
  person,
  refetch,
  requestId,
  resetForm,
  setupIntentId,
  settings,
}: PaymentPortalStepperProps) => {
  const [showAttachmentAuth, setShowAttachmentAuth] = useState(false);
  const { showSuccessMessage, setPaymentSuccessfullyCompleted, setShowSuccessMessage } = useStripePortalPayment();
  const { paymentRequest, captchaToken, setPaymentRequest, setPortalStepperSettings, setCaptchaToken } =
    useStripeStepper();

  const { modalProps: confirmModalProps, triggerProps: confirmTriggerProps } = useModalControl();

  useEffect(() => {
    setPaymentRequest({
      ...paymentRequest,
      amount: convertToCents(amount || 0),
      origin,
      paymentId: setupIntentId,
      person: {
        ...paymentRequest?.person,
        ...(person?.id ? { id: person.id } : {}),
      },
    });
    setPortalStepperSettings({
      allowSaveACHOnFile: !!settings?.allowSaveACHOnFile,
      allowSaveCardOnFile: !!settings?.allowSaveCardOnFile,
      amountEditable: !!settings?.amountEditable,
      isLive: !!locationData?.isLive,
      oneTimeRequest: !!settings?.oneTimeRequest,
      skipAttachmentAuth: !!settings?.skipAttachmentAuth,
      showExpressElement: !!settings?.showExpressElement,
      skipCaptcha: !!captchaTokenFromProps,
    });
    setCaptchaToken(captchaTokenFromProps);
  }, []);

  // Handle Affirm redirect
  const stripe = useStripe();
  const alerts = useAlert();
  const { t } = useTranslation();

  const params: AffirmRedirectParams = useSearch();
  const navigate = useNavigate();

  const getRedirectPaymentIntent = async () => {
    if (!stripe || !params.payment_intent_client_secret) {
      return;
    }
    const { paymentIntent } = await stripe.retrievePaymentIntent(params?.payment_intent_client_secret || '');
    setPaymentRequest({
      ...paymentRequest,
      amount: params.amount,
      paymentId: params.paymentId,
      person: {
        firstName: params.firstName,
        lastName: params.lastName,
      },
      receiptEmail: params.receiptEmail,
    });
    if (
      (params.payment_intent && paymentIntent?.status === 'requires_payment_method') ||
      paymentIntent?.status === 'requires_action'
    ) {
      alerts.error({
        autoDismissAfter: 12000,
        message: t('There was an issue with your Affirm setup. Please use a different payment method and try again.'),
      });
    } else if (paymentIntent?.status === 'succeeded') {
      handleOnSuccess();
    }
  };

  useEffect(() => {
    if (params.payment_intent) {
      getRedirectPaymentIntent();
    }
  }, [stripe, params.payment_intent]);

  const successPayment: SuccessPayment = useMemo(
    () => ({
      amount: paymentRequest?.amount || 0,
      paymentId: paymentRequest?.paymentId,
      person: {
        firstName: paymentRequest?.person?.firstName,
        lastName: paymentRequest?.person?.lastName,
        email: paymentRequest?.person?.email,
      },
    }),
    [paymentRequest]
  );

  const handleOnSuccess = () => {
    if (isFunction(onSuccess)) {
      onSuccess?.(successPayment);
    } else {
      setPaymentSuccessfullyCompleted(true);
      setShowSuccessMessage(true);
    }
  };

  const { handleSubmit } = useSubmitPaymentStripe({
    onSuccess: handleOnSuccess,
    onFailure,
    onDisabled,
    origin,
    requestId,
    requireCaptcha: !settings?.oneTimeRequest && !captchaTokenFromProps,
  });

  const onSubmit = () =>
    handleSubmit({
      paymentRequestBody: paymentRequest || {},
      token: captchaToken,
    });

  const handleMakeNewPayment = () => {
    navigate({ search: {} });
    refetch?.();
    resetForm?.();
  };

  if (showAttachmentAuth && attachment) {
    return (
      <AttachmentAuth
        attachmentLink={attachment}
        personName={person?.name}
        onSuccess={() => setShowAttachmentAuth(false)}
        onCancel={() => setShowAttachmentAuth(false)}
      />
    );
  } else if (showSuccessMessage) {
    return (
      <SuccessCard
        payment={paymentRequest || {}}
        onClick={!settings?.oneTimeRequest ? handleMakeNewPayment : undefined}
      />
    );
  } else {
    return (
      <>
        <div css={styles.stepperContainer}>
          {amount && (
            <RequestedAmount
              amount={amount}
              action={
                attachment ? (
                  <OpenAttachmentButton
                    attachment={attachment}
                    onClick={() => setShowAttachmentAuth(true)}
                    skipAttachmentAuth={settings?.skipAttachmentAuth}
                  />
                ) : undefined
              }
            />
          )}
          <StripeStepperBody amount={amount || 0} onExpressSubmit={onSubmit} onSubmit={confirmTriggerProps.onClick} />
          <LogoWrapper>
            <StripeIcon css={styles.stripeIcon} />
          </LogoWrapper>
        </div>
        <PaymentConfirmationModal onConfirm={onSubmit} payment={paymentRequest || {}} {...confirmModalProps} />
      </>
    );
  }
};

export const StripePaymentPortalStepperHeader = ({
  locationData,
  showLocationInfo = true,
}: {
  locationData: LocationPortalData | undefined;
  showLocationInfo?: boolean;
}) => {
  return locationData && showLocationInfo && <LocationInformation locationData={locationData} />;
};

const STRIPE_DEV_KEY = appConfig.STRIPE_EXPRESS_TEST_API_KEY;

export const StripePaymentPortalStepper = ({ ...props }: PaymentPortalStepperProps) => {
  const [formId, setFormId] = useState<string>(genUUIDV4());
  const resetForm = () => setFormId(genUUIDV4());
  const { locationData, settings } = props;

  return (
    <StripeElementsPortalWrapper
      amount={props.amount || 50}
      onBehalfOf={props.onBehalfOf}
      paymentConfigName={props.paymentConfigName}
      stripeKey={!locationData?.isLive ? STRIPE_DEV_KEY : undefined}
      isLive={locationData?.isLive}
    >
      <PortalPageWrapper Footer={<StripePrivacyPolicyFooter />}>
        <StripeStepperProvider key={formId}>
          <StripePaymentPortalStepperHeader locationData={locationData} showLocationInfo={settings?.showLocationInfo} />
          <StripePaymentPortalStepperContainer {...props} resetForm={resetForm} />
        </StripeStepperProvider>
      </PortalPageWrapper>
    </StripeElementsPortalWrapper>
  );
};
